import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {HeaderFilter} from 'asax-common';
import {filter, switchMap, tap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {TargetListLineResolved, TargetListService} from "../target-list.service";
import {TextSearchInput} from "../../search-input.model";

const defaultFileName = 'Untitled';

@Component({
  selector: 'asax-header-search-input-target-list',
  templateUrl: './target-list-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TargetListInputComponent implements OnInit, OnChanges {
  @ViewChild('targetListFileInput', {static: false}) targetListFileInput: ElementRef;
  @ViewChild('tooltip', {static: false}) tooltip: NgbTooltip;

  @Input() searchInput: TextSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  isInvalid: boolean;
  errorMessage: string;

  fileName: string;
  targetListLinesResolved: TargetListLineResolved[];

  private valueSubject = new Subject<any>();
  private valueIsValidatedSubject = new Subject();

  constructor(private readonly targetListService: TargetListService,
              private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => this.searchInput?.value?.length > 0),
      filter(() => this.isInvalid === false),
      tap(() => this.fileName = this.targetListFileInput?.nativeElement?.files[0]?.name || defaultFileName),
      tap(() => this.targetListFileInput.nativeElement.value = null),
      tap(() => this.tooltip?.open()),
      switchMap(() => this.targetListService.resolveTargetListString(this.searchInput?.value)),
      tap((targetListResolved) => {
        this.targetListLinesResolved = targetListResolved
      }),
      tap(() => this.tooltip?.close()),
      tap(() => this.emitFilter())
    ).subscribe();

    this.valueIsValidatedSubject.asObservable().pipe(
      filter(() => this.searchInput?.value?.length === 0),
      tap(() => this.targetListFileInput.nativeElement.value = null),
      tap(() => this.emitFilter())
    ).subscribe();

    this.valueSubject.asObservable().pipe(
      tap(() => this.isInvalid = undefined),
      tap(() => this.fileName = undefined),
      tap(() => this.targetListLinesResolved = undefined),
      tap(() => this.validateValue()),
      tap(() => this.changeDetectorRef.markForCheck()),
      tap(() => this.valueIsValidatedSubject.next(undefined))
    ).subscribe();
  }

  private validateValue(): void {
    const isAlphaNumeric = isTextAlphaNumeric(this.searchInput?.value);

    if (isAlphaNumeric === true) {
      this.isInvalid = false;
      this.errorMessage = '';
    } else {
      this.isInvalid = true;
      this.errorMessage = 'It seems file is not alphanumeric';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.valueChanged();
  }

  clearSearchInputValue(): void {
    this.searchInput.value = '';
    this.targetListFileInput.nativeElement.value = null;

    this.valueChanged();
  }

  openTargetListFileInput(): void {
    this.targetListFileInput?.nativeElement?.click();
  }

  targetListFileChanged(): void {
    const file = this.targetListFileInput?.nativeElement?.files[0];
    const fileReader = new FileReader();
    fileReader.onload = ({target}) => {
      this.searchInput.value = target?.result?.toString();
      this.valueSubject.next(undefined);
    }
    fileReader.readAsText(file);
  }

  private valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  private emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput?.value,
      valueParsed: this.targetListLinesResolved?.filter(line => line.isValid)?.map(line => line.raDecRadius)?.join('|'),
      valueToDisplay: this.fileName,
      label: this.searchInput.label,
      isDisabled: this.searchInput?.isDisabled,
      isInvalid: this.isInvalid,
      elasticParserType: this.searchInput.elasticParserType
    };

    this.outFilter.emit(filter);
  }
}

const isTextAlphaNumeric = (text: string): boolean => {
  for (let i = 0; i < text?.length; i++) {
    const code = text.charCodeAt(i);
    if (code > 127) {
      return false;
    }
  }

  return true;
}
